import { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  IconButton
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel, GridPaginationModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, Search, RotateCcw } from 'lucide-react';
import { ProfileService, ProfileFilter } from '../../services/profileService'; // Importando interface ProfileFilter
import { Profile } from '../../types/profile'; // Ajustado path do tipo
import { green, red } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import { ptBRGridLocale, getDataGridStyles } from '../../utils/gridLocalization';
import axios from 'axios'; // Importar axios para checar o tipo de erro

// Objeto de tradução
const ptBRLocaleText = {
  MuiTablePagination: {
    labelRowsPerPage: 'Linhas por página:',
  },
};

const ProfilePage = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<Profile | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null); // Estado para erro específico do diálogo
  
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'Todos' | 'Ativo' | 'Inativo'>('Todos');
  const [filteredProfiles, setFilteredProfiles] = useState<Profile[]>([]);
  
  // Estado para paginação
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  const [totalRowCount, setTotalRowCount] = useState(0); // <<< Adicionar estado para contagem total
  
  // Estado para ordenação
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'createdAt',
      sort: 'desc',
    },
  ]);

  const navigate = useNavigate();
  const profileService = useMemo(() => new ProfileService(), []); // Usar useMemo
  const theme = useTheme();

  useEffect(() => {
    fetchProfiles();
  }, [paginationModel.page, paginationModel.pageSize, sortModel]); // Busca os perfis quando mudar a paginação ou ordenação

  const fetchProfiles = async () => {
    setLoading(true);
    setError(null); // Limpar erro anterior
    try {
      // Preparar os filtros para a API
      const filters: ProfileFilter = {
        page: paginationModel.page + 1, 
        pageSize: paginationModel.pageSize,
        // Adicionar filtros de texto/status se aplicável aqui
        name: searchText || undefined, // Incluir filtro de nome
        ativo: statusFilter === 'Ativo' ? true : statusFilter === 'Inativo' ? false : undefined, // Incluir filtro de status
      };
      
      // Adiciona ordenação se existir
      if (sortModel.length > 0) {
        filters.sortField = sortModel[0].field;
        filters.sortOrder = sortModel[0].sort === 'asc' ? 'asc' : 'desc';
      }
      
      // Chamar o service que agora retorna { data, totalCount }
      const response = await profileService.getAll(filters);
      setProfiles(response.data); // Atualiza os dados da página atual
      setTotalRowCount(response.totalCount); // <<< Atualiza a contagem total
      // setFilteredProfiles(response.data); // Remover, não necessário com paginação server-side
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Falha ao carregar perfis. Tente novamente mais tarde.');
      setProfiles([]); // Limpar dados em caso de erro
      setTotalRowCount(0); // Zerar contagem em caso de erro
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate('/profiles/new');
  };

  const handleEdit = (id: number) => {
    navigate(`/profiles/edit/${id}`);
  };

  const handleDeleteClick = (profile: Profile) => {
    setProfileToDelete(profile);
    setDeleteError(null); // Limpa erro anterior ao abrir diálogo
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!profileToDelete) return;
    
    setDeleteError(null); // Limpar erro antes de tentar
    try {
      await profileService.delete(profileToDelete.id);
      // Sucesso - fechar diálogo, limpar seleção e recarregar lista
      setDeleteDialogOpen(false);
      setProfileToDelete(null);
      fetchProfiles(); // Recarrega a lista
      // Poderia adicionar um Snackbar/Toast de sucesso aqui

    } catch (err: any) {
      console.error("Erro ao excluir perfil:", err);
      // Verificar se é um erro do Axios com resposta
      if (axios.isAxiosError(err) && err.response) {
        // Verificar se o status é 409 (Conflict)
        if (err.response.status === 409) {
          // Erro específico de perfil em uso
          setDeleteError(err.response.data?.message || 'Este perfil está em uso e não pode ser excluído.');
        } else {
          // Outros erros HTTP
          setDeleteError(err.response.data?.message || `Erro ${err.response.status} ao excluir perfil.`);
        }
      } else {
        // Erro genérico (rede, etc.)
        setDeleteError('Falha na comunicação ao tentar excluir o perfil.');
      }
      // Não fechar o diálogo em caso de erro, para mostrar a mensagem
      // setDeleteDialogOpen(false); 
      // setProfileToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProfileToDelete(null);
    setDeleteError(null); // Limpa erro ao cancelar
  };

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value as 'Todos' | 'Ativo' | 'Inativo');
  };

  const handleSearch = async () => {
    // Resetar para a primeira página ao buscar
    setPaginationModel(prev => ({ ...prev, page: 0 })); 
    // fetchProfiles será chamado pelo useEffect devido à mudança no paginationModel
    // ou chamar fetchProfiles() diretamente se não quiser resetar página
    fetchProfiles(); // Chamar diretamente após ajustar filtros se necessário
  };

  const handleClear = async () => {
    setSearchText('');
    setStatusFilter('Todos');
    setPaginationModel({ page: 0, pageSize: 10 }); // Resetar paginação também
    // fetchProfiles será chamado pelo useEffect
  };

  // Manipulador unificado para paginação
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  // Manipula mudanças na ordenação
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'ativo',
      headerName: 'Status',
      width: 80,
      renderCell: (params: GridRenderCellParams<any, Profile>) => (
        <Chip 
          label={params.value ? 'Ativo' : 'Inativo'} 
          color={params.value ? 'success' : 'error'}
          size="small"
          sx={{ 
            fontSize: '0.75rem', 
            height: '22px',
            fontWeight: 'bold',
            color: 'white',
            '& .MuiChip-label': {
              padding: '0px 8px'
            }
          }}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Data de Criação',
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        return params.value ? new Date(params.value).toLocaleString('pt-BR') : '-';
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Data de Alteração',
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        return params.value ? new Date(params.value).toLocaleString('pt-BR') : '-';
      },
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Button
            onClick={() => handleEdit(params.row.id)}
            sx={{ minWidth: 'auto', mr: 0.5, p: '4px' }}
            title="Editar"
            size="small"
          >
            <Edit size={18} />
          </Button>
          <Button
            onClick={() => handleDeleteClick(params.row)}
            color="error"
            sx={{ minWidth: 'auto', p: '4px' }}
            title="Excluir/Inativar"
            disabled={!params.row.ativo}
            size="small"
          >
            <Trash2 size={18} />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      {/* Cabeçalho da Página: Título e Botão Novo */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Typography variant="h5" component="h1">
          Perfis de Acesso
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<Plus size={16} />}
          onClick={handleAddNew}
          size="small"
          sx={{ 
            py: 0.5, 
            px: 2, 
            height: 36, 
            fontWeight: 'bold',
            fontSize: '0.75rem',
            borderRadius: 1
          }}
        >
          NOVO PERFIL { /* Texto em maiúsculas */}
        </Button>
      </Box>
      
      {/* Filtros */}
      <Box sx={{ 
          mb: 1.5, 
          p: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius
      }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={6} md={5}>
              <TextField
                fullWidth 
                label="Buscar por Nome do Perfil"
                variant="outlined" 
                value={searchText} 
                onChange={handleSearchTextChange} 
                size="small"
                margin="dense"
                InputProps={{ 
                  style: { fontSize: '0.85rem' },
                  sx: { py: 0.3, height: 36 },
                  startAdornment: <Search size={16} style={{ marginRight: 8, color: '#666' }} />,
                }}
                InputLabelProps={{ 
                  style: { fontSize: '0.85rem' } 
                }}
                placeholder="Buscar por Nome do Perfil"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel id="status-filter-label" sx={{ fontSize: '0.85rem' }}>Status</InputLabel>
                <Select
                  labelId="status-filter-label" 
                  value={statusFilter} 
                  label="Status"
                  onChange={handleStatusFilterChange}
                  sx={{ 
                    fontSize: '0.85rem',
                    '.MuiSelect-select': { py: 0.5 },
                    height: 36
                  }}
                >
                  <MenuItem value="Todos" sx={{ fontSize: '0.85rem' }}>Todos</MenuItem>
                  <MenuItem value="Ativo" sx={{ fontSize: '0.85rem' }}>Ativo</MenuItem>
                  <MenuItem value="Inativo" sx={{ fontSize: '0.85rem' }}>Inativo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  size="small"
                  sx={{ 
                    py: 0.5, 
                    px: 2,
                    height: 36,
                    fontWeight: 'bold', 
                    fontSize: '0.75rem',
                    borderRadius: 1
                  }}
                  startIcon={<Search size={16} />}
                >
                  BUSCAR
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  size="small"
                  sx={{ 
                    py: 0.5, 
                    px: 2,
                    height: 36,
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    borderRadius: 1
                  }}
                  startIcon={<RotateCcw size={16} />}
                >
                  LIMPAR
                </Button>
              </Stack>
            </Grid>
          </Grid>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Card>
        <CardContent sx={{ p: 0, '& .MuiDataGrid-root': { border: 'none' } }}>
          <DataGrid
            rows={profiles}
            columns={columns}
            loading={loading}
            
            // Configuração de Paginação Nova
            paginationMode="server" 
            rowCount={totalRowCount} // Precisa do total real de itens do backend
            paginationModel={paginationModel} // Controla page e pageSize
            onPaginationModelChange={handlePaginationModelChange} // Atualiza o estado
            pageSizeOptions={[5, 10, 25, 50]} // Opções de tamanho de página
            
            // Configuração de Ordenação (inalterada)
            sortingMode="server" 
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            
            localeText={ptBRGridLocale}
            sx={getDataGridStyles(theme)}
            density="compact"
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      {/* Diálogo de Confirmação de Exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ fontSize: '1.1rem' }}>
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ fontSize: '0.9rem' }}>
            Tem certeza que deseja excluir permanentemente o perfil "{profileToDelete?.name}"? 
            Esta ação não pode ser desfeita.
          </DialogContentText>
          {/* Mostrar erro específico da exclusão aqui */}
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2, fontSize: '0.85rem' }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ pb: 1.5, px: 2 }}>
          <Button 
            onClick={handleDeleteCancel} 
            color="primary"
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            autoFocus
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            Excluir Permanentemente
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePage; 